import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import useAppDispatch from "hooks/useAppDispatch";
import { login } from "../../../../redux/features/auth/authSlice";
import { isEmail } from "utils/validation";
import { getCookie, hasForceLogout } from "utils/cookie";
import { getAppIdFromStorage } from "utils/appIdStorage";
import { isLoginDomain } from "utils/environment";
import {
  buildForgotPath,
  handlePostLoginRedirect,
  isDevOrLocalhostAuth,
  isLocalhostHost,
} from "../utils/authRedirect";

export default function useLogInPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const loggingIn = useSelector((state) => state.auth.loggingInLoading);
  const resumeAttemptedRef = useRef(false);

  const [step, setStep] = useState("credentials");
  const [signingIn, setSigningIn] = useState(false);
  const [passwordChecked, setPasswordChecked] = useState(false);
  const [email, setEmail] = useState(() => {
    const fromQuery = new URLSearchParams(location.search).get("email");
    return fromQuery ? String(fromQuery).trim() : "";
  });
  const [errorEmail, setErrorEmail] = useState(false);
  const [password, setPassword] = useState("");
  const [errorPassword, setErrorPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [twoFaCode, setTwoFaCode] = useState("");
  const [twoFaError, setTwoFaError] = useState("");

  const handleLoginSuccess = useCallback(
    (res) => {
      setSigningIn(true);
      handlePostLoginRedirect(res, navigate);
    },
    [navigate]
  );

  useEffect(() => {
    if (resumeAttemptedRef.current) return;
    const params = new URLSearchParams(location.search);
    if (params.has("auth_error")) return;
    // After password reset we pass ?email= so the user can sign in again.
    if (params.get("email")) return;
    if (hasForceLogout()) return;
    // Cookie resume only on the login host (or localhost). On authorize/tax /login
    // it used to bounce `/` ↔ `/login` while isLogged was still false.
    if (!isLoginDomain() && !isLocalhostHost()) return;
    // Kicked here with a dead session — stay on the form, do not hop back.
    const token = getCookie("api_key") || getCookie("API-KEY");
    if (!token) return;

    resumeAttemptedRef.current = true;
    setSigningIn(true);
    handlePostLoginRedirect({ app_id: getAppIdFromStorage() }, navigate);
  }, [location.search, navigate]);

  const validateEmail = useCallback(
    (value) => {
      const normalized = String(value ?? "").trim();
      if (normalized.length > 0 && !isEmail(normalized)) {
        setErrorEmail(t("login.invalidEmail"));
      } else {
        setErrorEmail(false);
      }
    },
    [t]
  );

  const resolveLoginError = useCallback(
    (error, fallbackKey) => {
      const payload = error?.payload ?? error;
      if (typeof payload === "string") {
        return payload;
      }
      if (payload?.error) {
        return payload.error;
      }
      return t(fallbackKey);
    },
    [t]
  );

  const doLogin = useCallback(async () => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail || !password) {
      setErrorPassword(t("login.enterPassAndEmail"));
      return;
    }
    if (normalizedEmail !== email) {
      setEmail(normalizedEmail);
    }

    try {
      const payload = await dispatch(
        login({ email: normalizedEmail, password, rememberMe: passwordChecked })
      );

      if (payload?.error) {
        setErrorPassword(
          typeof payload.error === "string"
            ? payload.error
            : t("auth.login.emailOrPasswordIncorrect")
        );
        return;
      }

      if (payload?.require_two_factor === true) {
        setStep("twoFactor");
        setTwoFaCode("");
        setTwoFaError("");
        return;
      }

      handleLoginSuccess(payload);
    } catch (error) {
      setSigningIn(false);
      setErrorPassword(
        resolveLoginError(error, "auth.login.emailOrPasswordIncorrect")
      );
    }
  }, [
    dispatch,
    email,
    password,
    passwordChecked,
    t,
    handleLoginSuccess,
    resolveLoginError,
  ]);

  const submitTwoFactor = useCallback(async () => {
    if (!twoFaCode || twoFaCode.length !== 6) {
      return;
    }

    setTwoFaError("");

    try {
      const payload = await dispatch(
        login({
          email: email.trim(),
          password,
          code: twoFaCode,
          rememberMe: passwordChecked,
        })
      );

      if (payload?.error) {
        setTwoFaError(payload.error);
        return;
      }

      handleLoginSuccess(payload);
    } catch (error) {
      setSigningIn(false);
      setTwoFaError(
        resolveLoginError(error, "auth.twoFa.wrongVerificationCode")
      );
    }
  }, [
    dispatch,
    email,
    password,
    passwordChecked,
    twoFaCode,
    handleLoginSuccess,
    resolveLoginError,
  ]);

  const handleBackToCredentials = useCallback(() => {
    setStep("credentials");
    setTwoFaCode("");
    setTwoFaError("");
  }, []);

  const handleTwoFaCodeChange = useCallback((value) => {
    const digitsOnly = value.replace(/\D/g, "").slice(0, 6);
    setTwoFaCode(digitsOnly);
    setTwoFaError("");
  }, []);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handlePasswordChange = useCallback((value) => {
    setPassword(value);
    setErrorPassword(false);
  }, []);

  return {
    t,
    navigate,
    loggingIn,
    signingIn,
    step,
    passwordChecked,
    email,
    errorEmail,
    password,
    errorPassword,
    showPassword,
    twoFaCode,
    twoFaError,
    isDevOrLocalhost: isDevOrLocalhostAuth(),
    setPasswordChecked,
    setEmail,
    validateEmail,
    togglePasswordVisibility,
    handlePasswordChange,
    doLogin,
    submitTwoFactor,
    handleBackToCredentials,
    handleTwoFaCodeChange,
    forgotPath: buildForgotPath(location.search),
    setErrorPassword,
    setTwoFaError,
  };
}
